import { CboRole, Res } from '@cbo/shared-library';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RequirePermission from '../../../auth/RequirePermission/RequirePermission';
import Header from '../../../components/Header/Header';
import { useCompanyLinks } from '../../../contexts/companyLinksContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useUsers } from '../../../contexts/userContext';
import { useCompanyLinksDataQuery } from '../../requests/queries';
import CompanyLinkActionDialog from '../CompanyLinkActionDialog/CompanyLinkActionDialog';
import CompanyLinksDataGrid, { CompanyLinkRowData } from './CompanyLinksDataGrid';

interface CompanyLinksTableProps {
  setIsCreatingOrEditingLink: (isCreatingOrEditingLink: boolean) => void;
  setLinkToEdit: (linkToEdit: Res.Firebase.Link) => void;
}

export interface CompanyLinkData {
  id: string;
  displayName: string;
  url: string;
}

function CompanyLinksTable({ setIsCreatingOrEditingLink, setLinkToEdit }: CompanyLinksTableProps) {
  const { t } = useTranslation();
  const [selectedLink, setSelectedLink] = useState<CompanyLinkData>();
  const [companyLinkActionOpen, setCompanyLinkActionOpen] = useState(false);
  const [rows, setRows] = useState<CompanyLinkRowData[]>([]);
  const { links, areLinksLoading, setLinks, setSidebarLinks, setLinksLoading } = useCompanyLinks();
  const { setSnackbarState } = useSnackbar();
  const user = useUsers();

  const handleLinkActionOpen = () => {
    setCompanyLinkActionOpen(true);
  };

  const handleCompanyLinkActionClose = () => {
    setCompanyLinkActionOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'displayName',
      headerName: t('admin.editLinksPage.linkText'),
      minWidth: 130,
      flex: 0.48,
    },
    {
      field: 'url',
      headerName: t('admin.editLinksPage.linkUrl'),
      minWidth: 150,
      flex: 1.08,
    },
    {
      field: 'viewers',
      headerName: t('admin.editLinksPage.viewers'),
      minWidth: 200,
      flex: 2.04,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('admin.editLinksPage.actions'),
      minWidth: 130,
      flex: 0.4,
      getActions: (params: GridRowParams<CompanyLinkRowData>) => [
        <RequirePermission permissions={[CboRole.LINK_ADMIN]}>
          <GridActionsCellItem
            data-testid={`edit-link-button-${params.row.id}`}
            icon={<Edit />}
            label={t('buttonText.edit')}
            onClick={() => {
              setLinkToEdit(params.row);
              setIsCreatingOrEditingLink(true);
            }}
          />
        </RequirePermission>,
        <RequirePermission permissions={[CboRole.LINK_ADMIN]}>
          <GridActionsCellItem
            data-testid={`delete-link-button-${params.row.id}`}
            icon={<Delete />}
            label={t('buttonText.delete')}
            onClick={() => {
              setSelectedLink({ id: params.row.id, displayName: params.row.displayName, url: params.row.url });
              handleLinkActionOpen();
            }}
          />
        </RequirePermission>,
      ],
    },
  ];

  // Get Company Links
  const {
    data: companyLinksData,
    isLoading: isCompanyLinksLoading,
    isError: isGetCompanyLinksError,
    refetch: refetchAllLinks,
  } = useCompanyLinksDataQuery(user.fullyAuthenticated === 'authenticated', false, user.org?.bslId);

  const {
    data: sidebarLinksData,
    isLoading: isSidebarLinksLoading,
    isError: isGetSidebarLinksError,
    refetch: refetchSideBarLinks,
  } = useCompanyLinksDataQuery(user.fullyAuthenticated === 'authenticated', true, user.org?.bslId);

  const refetchLinks = async () => {
    setLinksLoading(true);
    await refetchAllLinks();
    await refetchSideBarLinks();
    setLinksLoading(false);
  };

  const getOrganizationLinks = () => {
    setLinksLoading(true);
    if (!isCompanyLinksLoading && !isSidebarLinksLoading) {
      if (isGetCompanyLinksError || isGetSidebarLinksError) {
        setSnackbarState({ open: true, message: `Error fetching company links`, color: 'error' });
      }
      setSidebarLinks(sidebarLinksData ?? null);
      setLinks(companyLinksData ?? null);
      setLinksLoading(false);
    }
  };

  useEffect(() => {
    getOrganizationLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyLinksData,
    isCompanyLinksLoading,
    isSidebarLinksLoading,
    setLinks,
    setLinksLoading,
    setSidebarLinks,
    sidebarLinksData,
  ]);

  useEffect(() => {
    const mappedRowData: CompanyLinkRowData[] | undefined = links?.map(
      (link: Res.Firebase.Link) =>
        ({
          ...link,
          viewers: link.readRoles?.map((role) => role.description).join(', ') || 'Everyone',
        } as CompanyLinkRowData)
    );

    setRows(mappedRowData ?? []);
  }, [links]);

  return (
    <Box>
      <Grid
        container
        sx={{
          paddingX: 4,
        }}
      >
        <Grid item sm={12} justifyContent='flex-start'>
          {/* Header */}
          <Header
            dataTestId='company-links-title'
            title={t('admin.editLinksPage.companyLinksTitle')}
            description={t('admin.editLinksPage.companyLinksTitleHelper')}
            maxDescriptionWidth='600px'
            action={
              <RequirePermission permissions={[CboRole.LINK_ADMIN]}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    data-testid='add-new-link-button'
                    variant='contained'
                    size='large'
                    onClick={() => setIsCreatingOrEditingLink(true)}
                  >
                    {t('admin.editLinksPage.addNewLink')}
                  </Button>
                </Box>
              </RequirePermission>
            }
          />
          {/* Table */}
          <Box data-testid='all-company-links-table'>
            <CompanyLinksDataGrid
              isLoading={areLinksLoading}
              columns={columns}
              rows={rows}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'linkText', sort: 'asc' }],
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>
      <CompanyLinkActionDialog
        open={companyLinkActionOpen}
        companyLink={selectedLink}
        onClose={handleCompanyLinkActionClose}
        onDeleteAction={refetchLinks}
      />
    </Box>
  );
}

export default CompanyLinksTable;
